/**
 * Claims carried by the scoped funding-source link token (minted by
 * partnerships-api `POST /v2/accounts/link`, Melio-Entity-Id header).
 *
 * The token is a signed JWT — the portal does NOT verify the signature (the backend does
 * that on every authenticated call); it only base64url-decodes the payload to read the
 * embedded Plaid link_token and entity id needed to drive the flow.
 */
export type InternalAccountLinkClaims = {
  scope: string;
  partnerName: string;
  entityId: string;
  fundingSourceType: 'plaid' | 'card';
  /** Present on a verify link — the account whose micro-deposit verification is being completed. */
  accountId?: string;
  plaid?: { linkToken: string; expiration: string };
  /** Standard JWT expiry (seconds since epoch). */
  exp?: number;
};

const base64UrlDecode = (segment: string): string => {
  const padded = segment
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(segment.length / 4) * 4, '=');
  const binary = atob(padded);
  // Decode UTF-8 bytes (atob yields a binary string).
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/** Decodes the JWT payload (no signature verification). Returns null if the token is malformed. */
export function decodeInternalAccountLinkToken(token: string): InternalAccountLinkClaims | null {
  const segments = token.split('.');
  if (segments.length < 2) return null;
  try {
    return JSON.parse(base64UrlDecode(segments[1])) as InternalAccountLinkClaims;
  } catch {
    return null;
  }
}

/** True when the token carries an `exp` that is already in the past. */
export function isTokenExpired(claims: InternalAccountLinkClaims, nowMs: number = Date.now()): boolean {
  return typeof claims.exp === 'number' && claims.exp * 1000 <= nowMs;
}
